import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useEffect, useMemo, useState } from "react";
import { useFragment, useMutation } from "react-relay";

import { useRecurringRequestPreviewRefresh } from "@/features/RecurringRequests/RecurringRequestPreviewProvider";
import { RecurrenceFormValue } from "@/features/RecurringRequests/types";
import { RecurringRequestPreviewState } from "@/features/RecurringRequests/types";
import { useFlags } from "@/utils/launchDarkly";

import { useRecurringRequestSeriesPreviewMutation } from "./__generated__/useRecurringRequestSeriesPreviewMutation.graphql";
import { useRecurringRequestSeriesPreview_booking$key } from "./__generated__/useRecurringRequestSeriesPreview_booking.graphql";

const recurrenceInput = (recurrence: RecurrenceFormValue) =>
  recurrence.endCondition === "ON_DATE"
    ? recurrence.endDate
      ? {
          recurrenceType: recurrence.recurrenceType,
          repeatInterval: recurrence.repeatInterval,
          endCondition: "ON_DATE" as const,
          endDate: recurrence.endDate,
        }
      : null
    : {
        recurrenceType: recurrence.recurrenceType,
        repeatInterval: recurrence.repeatInterval,
        endCondition: "AFTER_OCCURRENCES" as const,
        occurrenceCount: recurrence.occurrenceCount ?? 1,
      };

type UseRecurringRequestSeriesPreviewArgs = {
  bookingRef: useRecurringRequestSeriesPreview_booking$key | null;
  pendingRecurrence: RecurrenceFormValue | null;
};

export const useRecurringRequestSeriesPreview = ({
  bookingRef,
  pendingRecurrence,
}: UseRecurringRequestSeriesPreviewArgs) => {
  const { revision, refresh } = useRecurringRequestPreviewRefresh();
  const booking = useFragment(
    useRecurringRequestSeriesPreviewBookingFragment,
    bookingRef
  );
  const { patronRecurringRequests } = useFlags();
  const [commit] = useMutation<useRecurringRequestSeriesPreviewMutation>(
    useRecurringRequestSeriesPreviewMutationString
  );
  const recurrence = useMemo(
    () => (pendingRecurrence ? recurrenceInput(pendingRecurrence) : null),
    [pendingRecurrence]
  );
  const [result, setResult] = useState<{
    key: string;
    recurrence: NonNullable<typeof recurrence>;
    state: RecurringRequestPreviewState;
  } | null>(null);
  const key =
    patronRecurringRequests && booking?.id && recurrence
      ? `${revision}|${booking.id}|${JSON.stringify(recurrence)}`
      : null;

  useEffect(() => {
    if (!key || !booking?.id || !recurrence) {
      setResult(null);
      return;
    }

    let isCurrent = true;
    const update = (state: RecurringRequestPreviewState) => {
      if (isCurrent) setResult({ key, recurrence, state });
    };
    const fail = (
      message = t`Unable to check recurring request dates. Please try again.`
    ) => update({ status: "error", preview: null, message });

    commit({
      variables: { input: { sourceBookingId: booking.id, recurrence } },
      onError: () => fail(),
      onCompleted: (response, errors) => {
        if (!isCurrent) return;

        const payload = response.responsePayload;
        if (errors?.length || !payload?.success || !payload.reviewedPlanToken) {
          fail(
            payload?.errors?.find((error) => error?.message)?.message ??
              undefined
          );
          return;
        }

        update({
          status: "success",
          preview: {
            reviewedPlanToken: payload.reviewedPlanToken,
            included:
              payload.included?.map((occurrence) => ({
                date: occurrence.date,
                warnings:
                  occurrence.warnings?.map((warning) => ({
                    type: warning.type,
                    label: warning.label,
                    startAt: warning.startAt,
                    endAt: warning.endAt,
                  })) ?? [],
              })) ?? [],
            excluded:
              payload.excluded?.map((exclusion) => ({
                date: exclusion.date,
                reason: exclusion.reason,
                requestOpensDate: exclusion.requestOpensDate,
                conflict: exclusion.conflict
                  ? {
                      type: exclusion.conflict.type,
                      label: exclusion.conflict.label,
                      startAt: exclusion.conflict.startAt,
                      endAt: exclusion.conflict.endAt,
                    }
                  : null,
              })) ?? [],
            proposedRemovals:
              payload.proposedRemovals?.map((removal) => ({
                date: removal.date,
              })) ?? [],
          },
        });
      },
    });

    return () => {
      isCurrent = false;
    };
  }, [booking?.id, commit, key, recurrence]);

  // Never expose an earlier result during the render before the new effect starts.
  const state: RecurringRequestPreviewState = !key
    ? { status: "idle", preview: null }
    : result?.key === key && result.recurrence === recurrence
    ? result.state
    : { status: "loading", preview: null };

  return { ...state, retry: refresh };
};

const useRecurringRequestSeriesPreviewBookingFragment = graphql`
  fragment useRecurringRequestSeriesPreview_booking on Booking {
    id
  }
`;

const useRecurringRequestSeriesPreviewMutationString = graphql`
  mutation useRecurringRequestSeriesPreviewMutation(
    $input: PreviewPatronRequestSeriesInput!
  ) {
    responsePayload: previewPatronRequestSeries(input: $input) {
      success
      errors {
        message
      }
      reviewedPlanToken
      included {
        date
        warnings {
          type
          label
          startAt
          endAt
        }
      }
      excluded {
        date
        reason
        requestOpensDate
        conflict {
          type
          label
          startAt
          endAt
        }
      }
      proposedRemovals {
        date
      }
    }
  }
`;
